/**
 * Equipment validation utilities
 */

export interface EquipmentFormData {
  name: string;
  type: string;
  minTempC: string;
  maxTempC: string;
}

export interface EquipmentValidationError {
  field: string;
  message: string;
}

/**
 * Validate equipment form data
 */
export function validateEquipmentForm(
  data: EquipmentFormData
): EquipmentValidationError[] {
  const errors: EquipmentValidationError[] = [];

  // Validate name
  if (!data.name.trim()) {
    errors.push({ field: 'name', message: 'Le nom de l\'équipement est requis' });
  } else if (data.name.trim().length < 2) {
    errors.push({ field: 'name', message: 'Le nom doit contenir au moins 2 caractères' });
  } else if (data.name.trim().length > 100) {
    errors.push({ field: 'name', message: 'Le nom doit contenir moins de 100 caractères' });
  }

  // Validate type
  if (!data.type.trim()) {
    errors.push({ field: 'type', message: 'Le type d\'équipement est requis' });
  }

  // Skip temperature validation for fryers
  if (!isFryerType(data.type)) {
    // An empty field means "no limit" (infinite) — only validate if provided
    const minProvided = data.minTempC.trim() !== '';
    const maxProvided = data.maxTempC.trim() !== '';

    let minTemp: number | null = null;
    if (minProvided) {
      minTemp = parseFloat(data.minTempC);
      if (isNaN(minTemp)) {
        errors.push({ field: 'minTempC', message: 'La température minimum doit être un nombre' });
      } else if (minTemp < -50) {
        errors.push({ field: 'minTempC', message: 'La température minimum ne peut pas être inférieure à -50°C' });
      } else if (minTemp > 50) {
        errors.push({ field: 'minTempC', message: 'La température minimum ne peut pas dépasser 50°C' });
      }
    }

    let maxTemp: number | null = null;
    if (maxProvided) {
      maxTemp = parseFloat(data.maxTempC);
      if (isNaN(maxTemp)) {
        errors.push({ field: 'maxTempC', message: 'La température maximum doit être un nombre' });
      } else if (maxTemp < -50) {
        errors.push({ field: 'maxTempC', message: 'La température maximum ne peut pas être inférieure à -50°C' });
      } else if (maxTemp > 50) {
        errors.push({ field: 'maxTempC', message: 'La température maximum ne peut pas dépasser 50°C' });
      }
    }

    // Validate temperature range
    if (minTemp !== null && maxTemp !== null && !isNaN(minTemp) && !isNaN(maxTemp)) {
      if (minTemp >= maxTemp) {
        errors.push({
          field: 'maxTempC',
          message: 'La température maximum doit être supérieure à la température minimum',
        });
      }
    }
  }

  return errors;
}

/**
 * Get equipment type suggestions
 */
export const EQUIPMENT_TYPES = [
  'Réfrigérateur',
  'Congélateur',
  'Chambre froide',
  'Vitrine réfrigérée',
  'Congélateur coffre',
  'Cellule de refroidissement',
  'Friteuse',
  'Sous vide',
  'Four',
  'Lave-vaisselle',
  'Autre',
];

export const FRYER_TYPE = 'Friteuse';

export function isFryerType(type: string): boolean {
  return type === FRYER_TYPE;
}

/** Map sector name to an emoji icon */
export function getSectorIcon(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('cuisine')) return '🍳';
  if (n.includes('toilette') || n.includes('wc') || n.includes('sanitaire')) return '🚽';
  if (n.includes('livraison')) return '📮';
  if (n.includes('réfrigér') || n.includes('froid')) return '❄️';
  if (n.includes('congél')) return '🧊';
  if (n.includes('cuisson') || n.includes('four') || n.includes('chaud')) return '🔥';
  if (n.includes('stockage') || n.includes('réserve')) return '📦';
  if (n.includes('zone de service') || n.includes('service')) return '👨‍🍳';
  if (n.includes('salle')) return '🍽️';
  if (n.includes('plonge') || n.includes('lavage') || n.includes('vaisselle')) return '🚿';
  if (n.includes('frit') || n.includes('huile')) return '🍟';
  if (n.includes('prépar') || n.includes('découpe')) return '🔪';
  if (n.includes('boulangerie') || n.includes('pâtisserie')) return '🥐';
  if (n.includes('boisson') || n.includes('bar')) return '🥤';
  return '🧹';
}

/** Map sector name to a color class set */
export function getSectorColor(name: string): { bg: string; border: string; text: string; dot: string } {
  const n = name.toLowerCase();
  if (n.includes('réfrigér') || n.includes('froid')) return { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', dot: 'bg-sky-500' };
  if (n.includes('congél')) return { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500' };
  if (n.includes('cuisson') || n.includes('four') || n.includes('chaud')) return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' };
  if (n.includes('stockage') || n.includes('réserve')) return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' };
  if (n.includes('service') || n.includes('salle')) return { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' };
  if (n.includes('plonge') || n.includes('lavage') || n.includes('vaisselle')) return { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', dot: 'bg-cyan-500' };
  if (n.includes('frit') || n.includes('huile')) return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' };
  if (n.includes('prépar') || n.includes('découpe')) return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' };
  return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-500' };
}

/** Map equipment type to an emoji icon */
export function getEquipmentTypeIcon(type: string): string {
  if (type === 'Réfrigérateur' || type === 'Chambre froide' || type === 'Vitrine réfrigérée') return '❄️';
  if (type === 'Congélateur' || type === 'Congélateur coffre') return '🧊';
  if (type === 'Friteuse') return '🍟';
  if (type === 'Four') return '🔥';
  if (type === 'Cellule de refroidissement') return '🌡️';
  if (type === 'Lave-vaisselle') return '🫧';
  if (type === 'Sous vide') return '📦';
  return '⚙️';
}
